<template>
  <div class="min-h-screen bg-slate-50 py-10 px-4">
    <div class="mx-auto max-w-3xl space-y-6">
      <div class="rounded-2xl bg-gradient-to-r from-brand to-brand/80 px-6 py-5 text-white shadow-lg shadow-brand/20">
        <h1 class="text-2xl font-semibold">Settings</h1>
        <p class="text-sm text-white/80">Manage your account security.</p>
      </div>

      <div class="rounded-2xl border border-slate-100 bg-white p-6 shadow-md shadow-slate-200/50">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h2 class="text-lg font-semibold text-slate-800">Update password</h2>
            <p class="text-sm text-slate-500">Use at least 8 characters. Avoid reusing old passwords.</p>
          </div>
        </div>

        <div v-if="error" class="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm">
          {{ errorMessage }}
        </div>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-1" for="currentPassword">Current password</label>
            <input id="currentPassword" type="password" v-model="currentPassword" class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" />
          </div>
          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-1" for="newPassword">New password</label>
              <input id="newPassword" type="password" v-model="newPassword" class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-1" for="newPasswordConfirm">Confirm password</label>
              <input id="newPasswordConfirm" type="password" v-model="newPasswordConfirm" class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-brand focus:ring-1 focus:ring-brand" />
            </div>
          </div>
          <div class="pt-2">
            <button @click="submitPasswordChange" class="inline-flex items-center justify-center rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand/20 transition hover:bg-brand/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40">
              Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import companyService from '@/services/marketplace/company.service';

export default {
  inject: ['toggleLoading'],
  data() {
    return {
      currentPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
      error: false,
      errorMessage: '',
      fadeTimeout: null,
    };
  },
  methods: {
    showError(message) {
      this.error = true;
      this.errorMessage = message;
      if (this.fadeTimeout) {
        clearTimeout(this.fadeTimeout);
      }
      this.fadeTimeout = setTimeout(() => {
        this.error = false;
      }, 5000);
    },
    async validatePasswordChangeForm() {
      this.error = false;
      this.errorMessage = '';

      let isValid = true;

      if (!this.currentPassword || !this.newPassword || !this.newPasswordConfirm) {
        this.errorMessage = 'Please fill out all the required password fields.';
        isValid = false;
      }

      if (this.newPassword === this.currentPassword) {
        this.errorMessage = 'The new password cannot be the same as your current password.';
        isValid = false;
      }

      if (this.newPassword.length < 8) {
        this.errorMessage = 'The new password must be at least 8 characters long.';
        isValid = false;
      }

      if (this.newPassword !== this.newPasswordConfirm) {
        this.errorMessage = 'The new password and the confirmation password do not match.';
        isValid = false;
      }

      if (!isValid) {
        this.$root.notify(this.errorMessage, 'error');
      }

      return isValid;
    },
    async submitPasswordChange() {
      this.toggleLoading(true);
      const isValid = await this.validatePasswordChangeForm();

      if (!isValid) {
        this.toggleLoading(false);
        return;
      }

      const postObject = {
        setting: 'password',
        currentPassword: this.currentPassword,
        newPassword: this.newPassword,
        newPasswordConfirm: this.newPasswordConfirm
      };

      companyService.updateCompanySettings(postObject)
        .then(response => {
          if (response.data && response.data.error) {
            this.$root.notify(response.data.errorMessage || 'Unable to update your password.', 'error');
          } else {
            this.$root.notify('Your password has been successfully updated!', 'success');
            this.currentPassword = '';
            this.newPassword = '';
            this.newPasswordConfirm = '';
          }
        })
        .catch(() => {
          this.$root.notify('Something went wrong while updating your account settings, please try again', 'error');
        })
        .finally(() => {
          this.toggleLoading(false);
        });
    },
  }
}
</script>
